<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import type { InvoiceLine, Recipe, RecipeLineMatch } from "../helpers/types";

  export let allInvoiceLines: InvoiceLine[] = [];
  export let connectedLines: RecipeLineMatch[] = [];
  export let index: number;
  export let connectedRecipes: Recipe[] = [];
  let line: RecipeLineMatch;
  let searchQuery: string = "";
  let searchResults: InvoiceLine[];
  let isOpen = false;

  let optionsContainer: HTMLDivElement | null = null;
  let searchInput: HTMLInputElement | null = null;

  const dispatch = createEventDispatcher();

  const handleClick = (link: InvoiceLine) => {
    if (!connectedLines[index].invoice_lines) {
      connectedLines[index].invoice_lines = [];
    }
    connectedLines[index].invoice_lines.push(link);
    dispatch("updateConnectedLines", connectedLines);
  };

  const unlink = (result: InvoiceLine) => {
    const i: number = line.invoice_lines.findIndex((c) => {
      return c.invoice_line_item_id === result.invoice_line_item_id;
    });
    connectedLines[index].invoice_lines.splice(i, 1);
    dispatch("updateConnectedLines", connectedLines);
  };

  const search = async () => {
    if (searchQuery.length === 0) {
      searchResults = allInvoiceLines;
      return;
    }

    const queryParts = searchQuery.toLowerCase().split(" ");
    const results = [];

    for (const key in allInvoiceLines) {
      const lineName = allInvoiceLines[key].invoice_line_item_name.toLowerCase();
      if (queryParts.every((part) => lineName.includes(part))) {
        results.push(allInvoiceLines[key]);
      }
    }

    results.sort((a, b) => a.invoice_line_item_name.localeCompare(b.invoice_line_item_name));

    searchResults = results;
  };

  $: {
    if (isOpen && optionsContainer) {
      document.addEventListener("click", handleDocumentClick);
    } else if (optionsContainer) {
      document.removeEventListener("click", handleDocumentClick);
    }
  }

  const handleDocumentClick = (event: MouseEvent) => {
    if (optionsContainer && !optionsContainer.contains(event.target as Node)) {
      isOpen = false;
    }
  };

  $: line = connectedLines[index];
  $: searchResults = allInvoiceLines;
</script>

<div class="ingredient-invoice-link">
  <div class="ingredient">
    {line.recipe_ingredient_item_name}
    {#if connectedRecipes.length}
      <div class="used-in">
        <div class="connected-recipe-badge">
          also used in
          {connectedRecipes.map((recipe) => recipe.name).join(", ")}
        </div>
      </div>
    {/if}
  </div>
  <!-- <svg viewBox="0 0 20 20" widht="20" height="20" xmlns="http://www.w3.org/2000/svg" fill="none">
    <g id="SVGRepo_bgCarrier" stroke-width="0" /><g
      id="SVGRepo_tracerCarrier"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <g id="SVGRepo_iconCarrier">
      <path
        stroke="#000000"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M2 10h16m0 0l-7-7m7 7l-7 7"
      />
    </g>
  </svg> -->
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div
    class="invoice-line cursor-oversized"
    on:click={(e) => {
      e.stopPropagation();
      isOpen = !isOpen;
      if (isOpen) {
        setTimeout(() => {
          searchInput?.focus();
        }, 10);
      }
    }}
  >
    <div class="form-control">
      <div class="connected-lines">
        {#if line.invoice_lines?.length > 0}
          {#each line.invoice_lines as connected}
            <div class="connected-badge">
              {allInvoiceLines.find(
                (line) => line.invoice_line_item_id === connected.invoice_line_item_id
              )?.invoice_line_item_name}
            </div>
          {/each}
        {:else}
          <div class="no-links">click to link invoice lines</div>
        {/if}
      </div>
      {#if isOpen}
        <div class="options-container" bind:this={optionsContainer}>
          <input
            autocomplete="off"
            class="invoice-line-search"
            class:options-visible={searchResults.length}
            id="input"
            bind:value={searchQuery}
            bind:this={searchInput}
            on:input={search}
            on:click={(e) => {
              e.stopPropagation();
            }}
            placeholder="start typing name of ingredient"
          />
          {#if line.invoice_lines?.length > 0}
            {#each line.invoice_lines as connected}
              <div class="option connected">
                <div
                  class="unlink"
                  on:click={(e) => {
                    e.stopPropagation();
                    unlink(connected);
                  }}
                >
                  &times;
                </div>
                <div class="invoice-line-name">{connected.invoice_line_item_name}</div>
              </div>
            {/each}
          {/if}
          {#each searchResults as result (result)}
            {#if !line.invoice_lines?.find((c) => c.invoice_line_item_id === result.invoice_line_item_id)}
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <div
                class="option"
                on:click={(e) => {
                  e.stopPropagation();
                  handleClick(result);
                  searchQuery = "";
                  searchInput?.focus();
                }}
              >
                <div>
                  {result.invoice_line_item_name}
                  <div class="invoice-line-supplier-name">
                    <small>{result.supplier_name}</small>
                  </div>
                </div>
              </div>
            {/if}
          {/each}
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .ingredient-invoice-link {
    display: flex;
    border-radius: 14px;
    background-color: var(--rekki-neutral-01);
    font-size: 16px;
    gap: 10px;
  }
  .form-control {
    flex: 1;
    display: flex;
    position: relative;
  }

  .connected-lines {
    display: block;
    line-height: 0;
  }

  .badge {
    margin-right: 10px;
  }
  .options-container {
    position: absolute;
    max-height: 200px;
    overflow-y: auto;
    top: 100%;
    padding: 0 0 5px;
    background-color: #fff;
    border-radius: 6px;
    box-shadow: 4px 4px 9px rgba(0, 0, 0, 0.1);
    z-index: 2;
    max-width: 100%;
  }

  .options-container .option {
    padding: 4px 10px 3px;
    cursor: pointer;
    white-space: nowrap;
    width: 100%;
    display: flex;
    align-items: center;
    position: relative;
    box-sizing: border-box;
    gap: 4px;
    font-size: 14px;
    line-height: 1.2;
  }

  .option .unlink {
    cursor: pointer;
    background-color: var(--rekki-neutral-01);
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .options-container .option:hover {
    color: #fff;
    background-color: #000;
  }

  .invoice-line {
    flex: 2;
    padding: 10px 20px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  .ingredient {
    flex: 1;
    padding: 10px 20px;
    border-radius: 9px 0 0 9px;
    background-color: #fff;
    min-height: 80px;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: 6px 0 6px 6px;
  }

  .ingredient::after {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    left: 100%;
    top: 0;
    border-width: 40px 20px;
    border-style: solid;
    border-color: transparent;
    border-left-color: #fff;
  }

  .invoice-line-search {
    flex: 1;
    background-color: #fff;
    border: none;
    font-size: 16px;
    position: sticky;
    top: 0;
    z-index: 2;
    width: 100%;
    padding: 10px;
    box-sizing: border-box;
    border-bottom: 1px solid var(--rekki-neutral-01);
    margin-bottom: 5px;
  }
  .connected-badge {
    font-size: 13px;
    padding: 3px 6px 4px;
    border-radius: 3px;
    background-color: var(--rekki-neutral-03);
    margin: 1.5px;
    color: #fff;
    line-height: 1;
    display: inline-block;
  }
  .no-links {
    font-size: 13px;
    padding: 3px 6px 4px;
    border-radius: 3px;
    color: var(--rekki-neutral-02);
    background-color: transparent;
    margin: 1.5px;
    line-height: 1;
    display: inline-block;
  }
  .invoice-line-name {
    width: calc(100% - 30px);
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .used-in {
    font-size: 50%;
  }
  .connected-recipe-badge {
    padding: 3px 6px 4px;
    border-radius: 3px;
    background-color: var(--rekki-neutral-03);
    margin: 1.5px;
    color: #fff;
    line-height: 1;
    display: inline-block;
  }

  .invoice-line-supplier-name {
    opacity: 0.5;
    font-family: "REKKI-OCD";
    text-transform: uppercase;
    font-weight: normal;
  }
</style>
